import { useNavigate } from '@solidjs/router'
import { createEffect, createSignal, type Component } from 'solid-js'

import { AppRoutes } from '../shared/app-routes'
import ChorusLogo from '../assets/images/chorus-logo.svg'
import { userApiService } from '../features/auth/data/user-api.service'

export const LoginRoute: Component = () => {
  const navigate = useNavigate()
  const [email, setEmail] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null)

  createEffect(() => {
    const error = new URLSearchParams(window.location.search).get('error')
    if (error === 'insufficient_permissions') {
      setErrorMessage('You do not have sufficient permissions to access this application.')
    }
  })

  const canSubmit = () => email().trim().length > 0 && password().length > 0 && !isSubmitting()

  const hasOrganizations = async (): Promise<boolean> => {
    try {
      const me = await userApiService.getMe()
      const memberships = me.organization_memberships ?? []
      console.info(
        '[auth] /me organizations:',
        memberships.map((m) => m.organization_slug)
      )
      return memberships.length > 0
    } catch (err) {
      console.error('[auth] /me fetch failed:', err)
      return false
    }
  }

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()
    setErrorMessage(null)

    const identifier = email().trim()
    const pw = password()

    if (!identifier || !pw) {
      return
    }

    const clerk = window.Clerk
    const canCustomSignIn = !!clerk?.client?.signIn?.create && !!clerk?.setActive
    if (!canCustomSignIn) {
      setErrorMessage('Sign-in is not ready yet. Please refresh the page and try again.')
      return
    }

    setIsSubmitting(true)
    try {
      const attempt = await clerk.client!.signIn.create({ identifier, password: pw })

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await clerk.setActive!({ session: attempt.createdSessionId })

        const hasOrgs = await hasOrganizations()
        if (!hasOrgs) {
          const loginUrl = new URL(window.location.href)
          loginUrl.searchParams.set('error', 'insufficient_permissions')
          // Ensure we land back on the login route with a visible error message.
          await clerk.signOut({ redirectUrl: loginUrl.toString() })
          return
        }

        navigate(AppRoutes.Dashboard(), { replace: true })
        return
      }

      // Other statuses (e.g. needs_second_factor, needs_client_trust) need a step this form does not support.
      console.info('[auth] sign-in status:', attempt.status)
      setErrorMessage('Sign-in needs an extra verification step that this page cannot complete.')
    } catch (err) {
      // Clerk errors often include a structured "errors" array; we keep UX minimal here.
      console.error(err)
      setErrorMessage('Unable to sign in. Please check your email and password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div class="min-h-screen bg-gray-50">
      <div class="mx-auto flex min-h-screen max-w-sm items-center px-4 py-10">
        <div class="w-full">
          <div class="mb-10 flex flex-col items-center">
            <img src={ChorusLogo} alt="chorus" class="h-7 w-auto" />
            <h1 class="mt-8 text-center text-2xl font-semibold text-gray-900">Sign in to your account</h1>
          </div>

          <form class="space-y-6" onSubmit={handleSubmit}>
            <div class="space-y-4">
              <label class="block">
                <span class="block text-sm font-medium text-gray-900">Email address</span>
                <input
                  type="email"
                  autocomplete="email"
                  value={email()}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  class="mt-2 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </label>

              <label class="block">
                <span class="block text-sm font-medium text-gray-900">Password</span>
                <input
                  type="password"
                  autocomplete="current-password"
                  value={password()}
                  onInput={(e) => setPassword(e.currentTarget.value)}
                  class="mt-2 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </label>

              <p
                role="alert"
                aria-live="polite"
                class={`mt-1 min-h-4 text-xs ${errorMessage() ? 'text-red-600' : 'text-transparent'}`}
              >
                {errorMessage() ?? '—'}
              </p>
            </div>

            <button
              type="submit"
              disabled={!canSubmit()}
              class="w-full rounded-md bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting() ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
